import {Step} from "@/components/wizard/types";
import {onBeforeUnmount, onMounted, ref, watch} from "vue";
import {onBeforeRouteLeave} from "vue-router";

export function useWizard(options = {}) {
    const {startStepName = null} = options;

    /** @var {Map<string, Step>} steps */
    const steps = new Map();
    let startStep = null;
    const currentStep = ref(null);
    const history = ref([]);
    let endHandler = null;

    const backEnabled = ref(true);
    const pushedFlow = [];

    function defineStep(name, options = {}) {
        if (steps.has(name)) {
            throw new Error(`Step with name ${name} already defined.`);
        }

        const step = new Step(name, steps.size, options.next, options.enter, options.enableBack);
        steps.set(name, step);
        if (
            (!startStepName && steps.size === 1)
            || (startStepName && startStepName === name))
        {
            startStep = step;
            currentStep.value = step;
        }

        return step;
    }

    function onEnd(handler) {
        endHandler = handler;
    }

    function getStep(stepName) {
        return steps.get(stepName);
    }

    function goNext(eventData = undefined) {
        let nextStepName = null;

        if (pushedFlow.length > 0) {
            nextStepName = pushedFlow.shift();
            if (pushedFlow.length === 0) {
                backEnabled.value = true;
            }

            // Call the next handler but ignore the return value.
            if (currentStep.value.nextHandler) {
                currentStep.value.nextHandler(eventData);
            }

            currentStep.value = steps.get(nextStepName);
            return currentStep.value;
        }
        

        if (currentStep.value.nextHandler) {
            nextStepName = currentStep.value.nextHandler(eventData);
        }

        if (nextStepName === null || nextStepName === undefined) {
            nextStepName = null;
            const nextStepIndex = currentStep.value.index + 1;
            for (const [stepName, step] of steps) {
                if (step.index === nextStepIndex) {
                    nextStepName = stepName;
                    break;
                }
            }
        }

        if (nextStepName === null) {
            if (endHandler !== null) {
                endHandler(eventData);
            }
            return null;
        } else {
            history.value.push(currentStep.value);
            currentStep.value = steps.get(nextStepName);
            return currentStep.value;
        }
    }

    function canGoBack() {
        return backEnabled.value && currentStep.value.enableBack && history.value.length > 0;
    }

    function goBack() {
        if (!backEnabled.value || !currentStep.value.enableBack) {
            return null;
        }

        if (pushedFlow.length > 0) {
            currentStep.value = steps.get(pushedFlow[pushedFlow.length - 1]);
            pushedFlow.splice(0, pushedFlow.length);
            return currentStep.value;
        }

        if (history.value.length === 0) {
            return null;
        }

        currentStep.value = history.value.pop();
        return currentStep.value;
    }

    function pushFlow(stepNames, allowBack = true) {
        backEnabled.value = allowBack;
        const stepSeries = [...stepNames, currentStep.value.name];
        pushedFlow.unshift(...stepSeries);
        return goNext();
    }

    function reset() {
        history.value.splice(0, history.value.length);
        backEnabled.value = true;
        pushedFlow.splice(0, pushedFlow.length);
        currentStep.value = startStep;
    }

    watch(currentStep, (newValue, oldValue) => {
        if (newValue !== null && newValue !== undefined) {
            if (newValue.enterHandler) {
                newValue.enterHandler(newValue);
            }
        }
    });

    return {
        // State
        currentStep,

        // Functions for definition
        defineStep,
        onEnd,
        getStep,

        // Methods
        canGoBack,
        goNext,
        goBack,
        pushFlow,
        reset,
    };
}

export function useWizardLeaveConfirmation(wizard, formMeta, confirmationMessage, ignoredSteps = []) {
    const ignoredStepNames = ignoredSteps.map((step) => {
        if (typeof step === 'string') {
            return step;
        } else {
            return step.name;
        }
    });

    // Ask the user on navigation changes when there are any unsaved patient data.
    function beforeWindowUnload(event) {
        if (!formMeta.value.dirty || ignoredStepNames.includes(wizard.currentStep.value.name)) {
            return;
        }

        // Show a confirmation dialog if there are any pending changes.
        event.preventDefault();
        event.returnValue = true;
    }

    onMounted(() => {
        window.addEventListener('beforeunload', beforeWindowUnload);
    });
    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', beforeWindowUnload);
    });

    onBeforeRouteLeave(() => {
        if (!formMeta.value.dirty || ignoredStepNames.includes(wizard.currentStep.value.name)) {
            return;
        }

        const answer = window.confirm(confirmationMessage);
        // Cancel the navigation and stay on the same page.
        if (!answer) {
            return false;
        }
    });
}
